import { convertBorderToCSS } from '../convert-border-to-css';
import { convertMarginToCSS } from '../convert-margin-to-css';
import { convertPaddingToCSS } from '../convert-padding-to-css';
import { ensureUnit } from '../ensure-unit';
import type { StyleAttributes } from './types';

/**
 * Generate CSS string from style attributes
 *
 * @param {StyleAttributes} attributes - Block attributes containing style data
 * @param {string} blockId - Unique block identifier
 * @returns {string} CSS string
 */
export function generateCSSFromAttributes(
	attributes: StyleAttributes,
	blockId: string,
): string {
	const styles: string[] = [];
	// Check if blockId starts with 'redfox-block-' (class) or use as data attribute
	const selector = blockId.startsWith( 'redfox-block-' )
		? `.${ blockId }`
		: `[data-block-id="${ blockId }"]`;

	// Typography styles
	if ( attributes.style?.typography ) {
		const typography = attributes.style.typography;
		const typographyStyles: string[] = [];

		if ( typography.fontSize ) {
			typographyStyles.push(
				`font-size: ${ ensureUnit( typography.fontSize ) }`,
			);
		}
		if ( typography.fontWeight ) {
			typographyStyles.push( `font-weight: ${ typography.fontWeight }` );
		}
		if ( typography.textTransform ) {
			typographyStyles.push(
				`text-transform: ${ typography.textTransform }`,
			);
		}
		if ( typography.lineHeight ) {
			typographyStyles.push( `line-height: ${ typography.lineHeight }` );
		}
		if ( typography.letterSpacing ) {
			typographyStyles.push(
				`letter-spacing: ${ ensureUnit( typography.letterSpacing ) }`,
			);
		}

		if ( typographyStyles.length > 0 ) {
			styles.push(
				`${ selector } { ${ typographyStyles.join( '; ' ) }; }`,
			);
		}
	}

	// Color styles
	if ( attributes.style?.color ) {
		const color = attributes.style.color;
		const colorStyles: string[] = [];

		if ( color.text ) {
			colorStyles.push( `color: ${ color.text }` );
		}
		if ( color.background ) {
			colorStyles.push( `background-color: ${ color.background }` );
		}
		if ( color.border ) {
			colorStyles.push( `border-color: ${ color.border }` );
		}

		if ( colorStyles.length > 0 ) {
			styles.push( `${ selector } { ${ colorStyles.join( '; ' ) }; }` );
		}
	}

	// Spacing styles
	if ( attributes.style?.spacing ) {
		const spacing = attributes.style.spacing;
		const spacingStyles: string[] = [];

		if ( spacing.margin ) {
			const marginCSS = convertMarginToCSS( spacing.margin );
			Object.entries( marginCSS ).forEach( ( [ prop, value ] ) => {
				spacingStyles.push(
					`${ prop
						.replace( /([A-Z])/g, '-$1' )
						.toLowerCase() }: ${ value }`,
				);
			} );
		}

		if ( spacing.padding ) {
			const paddingCSS = convertPaddingToCSS( spacing.padding );
			Object.entries( paddingCSS ).forEach( ( [ prop, value ] ) => {
				spacingStyles.push(
					`${ prop
						.replace( /([A-Z])/g, '-$1' )
						.toLowerCase() }: ${ value }`,
				);
			} );
		}

		if ( spacingStyles.length > 0 ) {
			styles.push( `${ selector } { ${ spacingStyles.join( '; ' ) }; }` );
		}
	}

	// Border styles
	if ( attributes.style?.border ) {
		const borderCSS = convertBorderToCSS( attributes.style.border );
		const borderStyles: string[] = [];

		Object.entries( borderCSS ).forEach( ( [ prop, value ] ) => {
			borderStyles.push(
				`${ prop
					.replace( /([A-Z])/g, '-$1' )
					.toLowerCase() }: ${ value }`,
			);
		} );

		if ( borderStyles.length > 0 ) {
			styles.push( `${ selector } { ${ borderStyles.join( '; ' ) }; }` );
		}
	}

	// Layout styles
	if ( attributes.style?.layout ) {
		const layout = attributes.style.layout;
		const layoutStyles: string[] = [];

		if ( layout.display ) {
			layoutStyles.push( `display: ${ layout.display }` );
		}
		if ( layout.flexDirection ) {
			layoutStyles.push( `flex-direction: ${ layout.flexDirection }` );
		}
		if ( layout.justifyContent ) {
			layoutStyles.push( `justify-content: ${ layout.justifyContent }` );
		}
		if ( layout.alignItems ) {
			layoutStyles.push( `align-items: ${ layout.alignItems }` );
		}
		if ( layout.gap ) {
			layoutStyles.push( `gap: ${ ensureUnit( layout.gap ) }` );
		}

		if ( layoutStyles.length > 0 ) {
			styles.push( `${ selector } { ${ layoutStyles.join( '; ' ) }; }` );
		}
	}

	// Custom CSS
	if ( attributes.customCSS ) {
		const customStyles: string[] = [];
		Object.entries( attributes.customCSS ).forEach( ( [ prop, value ] ) => {
			if ( value ) {
				customStyles.push( `${ prop }: ${ value }` );
			}
		} );

		if ( customStyles.length > 0 ) {
			styles.push( `${ selector } { ${ customStyles.join( '; ' ) }; }` );
		}
	}

	return styles.join( '\n' );
}
